import MultiSelect from "@pages/DatabasePage/MultiSelect"
import { useQueryClient } from "@tanstack/react-query"
import { Button } from "@ui-lib/index"
import { ValueLabelPair } from "@utilityTypes/ValueLabelPair"
import { requestApi2 } from "@utils/http"
import { portalSocket } from "@utils/websocket/portalSocket"
import { seempSocket } from "@utils/websocket/seempSocket"
import { endpoints, SeempReport } from "api"
import { FormEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { getHandlePlantCacheUpdated } from "./SeempContent"
import { useSeempGetUsersQuery } from "./queries/useSeempGetUsersQuery"

export default function SeempUpdateReportBody({
	companyName,
	plantId,
	reportData,
	setModalOpenState,
}: {
	companyName: string
	plantId: number
	reportData: SeempReport
	setModalOpenState: (isOpen: boolean) => void
}) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const handlePlantCacheUpdated = getHandlePlantCacheUpdated(queryClient)
	const [newName, setNewName] = useState<string>(reportData.name)
	const [newYear, setNewYear] = useState<number>(reportData.year)
	const [newResponsibleUsers, setNewResponsibleUsers] = useState<string[] | undefined>(
		reportData.responsibleUsers
	)

	const {
		isLoading: isUsersListLoading,
		isError: isUsersListError,
		data: usersList,
	} = useSeempGetUsersQuery({ companyName })

	const usersOptions = usersList?.map((user) => ({
		value: user.email,
		label: `${user.name} (${user.email})`,
	}))
	const [responsibleUsers, setResponsibleUsers] = useState<ValueLabelPair[]>([])

	useEffect(() => {
		if (
			usersOptions === undefined ||
			usersOptions === null ||
			reportData.responsibleUsers === undefined ||
			reportData.responsibleUsers === null
		)
			return

		const originalUsers: ValueLabelPair[] | [] = usersOptions
			.filter((user) => reportData.responsibleUsers?.includes(user.value) == true)
			.map((user) => user)

		setResponsibleUsers(originalUsers)
	}, [usersList])

	const handleUpdateReport = async (
		newName: string,
		newYear: number,
		newResponsibleUsers: string[] | undefined
	) => {
		if (
			newName === reportData.name &&
			newYear === reportData.year &&
			reportData.responsibleUsers === newResponsibleUsers
		) {
			return
		}

		const reportUpdated = await requestApi2(endpoints.seempUpdateReport, {
			reportId: reportData.id,
			name: newName,
			year: newYear,
			isPublished: reportData.isPublished,
			responsibleUsers: newResponsibleUsers ?? [],
			socketId: portalSocket.getSocketId(),
		})
		if (reportUpdated === null) {
			alert(t("FAILED TO UPDATE REPORT"))
			setNewName("Seemp Report")
			setNewYear(new Date().getFullYear() + 1)
			return
		}

		seempSocket.sendInvalidatePlantCacheRequest(companyName, plantId)
		seempSocket.sendPlantCacheUpdateRequest(
			companyName,
			plantId,
			"update",
			{
				...reportData,
				name: newName,
				year: newYear,
				isPublished: reportData.isPublished,
				responsibleUsers: newResponsibleUsers ?? [],
			},
			reportData.id
		)
		handlePlantCacheUpdated({
			plantId,
			updateType: "update",
			data: {
				...reportData,
				name: newName,
				year: newYear,
				isPublished: reportData.isPublished,
				responsibleUsers: newResponsibleUsers ?? [],
			},
			reportId: reportData.id,
		})
		setNewName("Seemp Report")
		setNewYear(new Date().getFullYear() + 1)
		setModalOpenState(false)
	}
	const handleUpdatePublishedReport = async (
		newName: string,
		unpublish: boolean,
		newResponsibleUsers: string[] | undefined
	) => {
		if (
			newName === reportData.name &&
			unpublish === false &&
			reportData.responsibleUsers === newResponsibleUsers
		) {
			return
		}

		const reportUpdated = await requestApi2(endpoints.seempUpdateReport, {
			reportId: reportData.id,
			name: newName,
			year: newYear,
			isPublished: unpublish ? false : reportData.isPublished,
			responsibleUsers: newResponsibleUsers ?? [],
			socketId: portalSocket.getSocketId(),
		})
		if (reportUpdated === null) {
			alert(t("FAILED TO UPDATE REPORT"))
			setNewName("Seemp Report")
			setNewYear(new Date().getFullYear() + 1)
			return
		}

		seempSocket.sendInvalidatePlantCacheRequest(companyName, plantId)
		seempSocket.sendPlantCacheUpdateRequest(
			companyName,
			plantId,
			"update",
			{
				...reportData,
				name: newName,
				year: newYear,
				isPublished: unpublish ? false : reportData.isPublished,
				responsibleUsers: newResponsibleUsers ?? [],
			},
			reportData.id
		)
		handlePlantCacheUpdated({
			plantId,
			updateType: "update",
			data: {
				...reportData,
				name: newName,
				year: newYear,
				isPublished: unpublish ? false : reportData.isPublished,
				responsibleUsers: newResponsibleUsers ?? [],
			},
			reportId: reportData.id,
		})
		setNewName("Seemp Report")
		setNewYear(new Date().getFullYear() + 1)
		setModalOpenState(false)
	}

	const [unpublishReport, setUnpublishReport] = useState<boolean>(false)

	return (
		<div
			className={`shadow-card fixed inset-1/2 flex h-fit w-200 -translate-1/2 flex-col rounded-sm border-0 bg-white p-3`}
		>
			<p className={"text-dipai-secondary-901 text-menu h-8 font-semibold"}>
				{t("UPDATE SEEMP REPORT")}
			</p>
			<div className={`flex w-full flex-col justify-between gap-3`}>
				<div className={`flex flex-row gap-3`}>
					<div className={`flex w-[calc(100%-5rem)] flex-col gap-1.5`}>
						<label
							htmlFor="report-name"
							className={`text-tiny h-4`}
						>
							{t("REPORT NAME")}
						</label>
						<input
							id="report-name"
							onChange={(e: FormEvent<HTMLInputElement>) => {
								const target = e.target as HTMLInputElement
								setNewName(target.value)
							}}
							// onChange={debounce((e: FormEvent<HTMLInputElement>) => {
							// 	const target = e.target as HTMLInputElement
							// 	setReportName(target.value)
							// }, 250)}
							value={newName}
							className={`border-dipai-secondary-901/20 text-tiny hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 h-8 rounded-sm border-1 p-2 outline-none focus:ring-1`}
						/>
					</div>
					{reportData.isPublished ? (
						<div className={`flex w-100 flex-col gap-1.5`}>
							<label
								htmlFor="report-year"
								className={`text-tiny h-4`}
							>
								{t("UNPUBLISH REPORT")}
							</label>

							<div className="flex h-8 items-center gap-2">
								<input
									type="checkbox"
									id="confirm-unpublish"
									name="confirm-unpublish"
									checked={unpublishReport}
									onChange={() => setUnpublishReport(!unpublishReport)}
								/>
								<label
									htmlFor="confirm-unpublish"
									className="text-menu"
								>
									{t("UNPUBLISH REPORT")}
								</label>
							</div>
						</div>
					) : (
						<div className={`flex w-20 flex-col gap-1.5`}>
							<label
								htmlFor="report-year"
								className={`text-tiny h-4`}
							>
								{t("TARGET YEAR")}
							</label>
							<input
								id="report-year"
								type="number"
								min={new Date().getFullYear()}
								max={new Date().getFullYear() + 3}
								onChange={(e) => {
									setNewYear(Number(e.target.value))
								}}
								value={newYear}
								className={`border-dipai-secondary-901/20 text-tiny hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 h-8 rounded-sm border-1 p-2`}
							/>
						</div>
					)}

					<div className={`flex w-full flex-col gap-1.5`}>
						<label
							htmlFor="responsible-users"
							className={`text-tiny h-4`}
						>
							{t("RESPONSIBLE PERSONS")}
						</label>
						<MultiSelect
							options={usersOptions ?? []}
							value={responsibleUsers}
							onChange={(selectedOptions) => {
								setNewResponsibleUsers(
									selectedOptions.length > 0
										? selectedOptions.map((option) => option.value)
										: undefined
								)
								setResponsibleUsers(selectedOptions as ValueLabelPair[])
							}}
							isClearable={true}
							placeHolderText={t("SELECT THE RESPONSIBLE PERSONS")}
							maxAmountItems={10}
						/>
					</div>
				</div>
				<div className={`flex flex-row justify-end gap-3`}>
					<Button
						variant={"outline-dark"}
						onClick={() => setModalOpenState(false)}
					>
						<Button.Label>{t("CANCEL")}</Button.Label>
					</Button>
					{reportData.isPublished ? (
						<Button
							variant={"filled-secondary"}
							disabled={
								reportData.name === newName &&
								unpublishReport !== reportData.isPublished &&
								reportData.responsibleUsers === newResponsibleUsers
							}
							onClick={() =>
								handleUpdatePublishedReport(
									newName,
									unpublishReport,
									newResponsibleUsers
								)
							}
						>
							<Button.Label>{t("UPDATE")}</Button.Label>
						</Button>
					) : (
						<Button
							variant={"filled-secondary"}
							disabled={
								reportData.name === newName &&
								newYear === reportData.year &&
								reportData.responsibleUsers === newResponsibleUsers
							}
							onClick={() =>
								handleUpdateReport(newName, newYear, newResponsibleUsers)
							}
						>
							<Button.Label>{t("UPDATE")}</Button.Label>
						</Button>
					)}
				</div>
			</div>
		</div>
	)
}
